// Clientes.js - VERSÃO COMPLETA CORRIGIDA
// 🔥 CORRIGIDO: carregarMetricas usa a instância `api` /api)
//    em vez de API_BASE_URL (que aponta para /api — causava 404)
// 🔥 CORRIGIDO: adicionado state `exportandoMetricas` e função `exportarMetricasCliente`

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
    BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer
} from 'recharts';
import './Clientes.css';
import { Icon } from './SharedComponents';




// 🔥 CONFIGURAÇÃO PADRÃO (fallback)
const CONFIG_PADRAO = {
    moeda: {
        simbolo: 'MT',
        codigo: 'MZN',
        casasDecimais: 2,
        separadorDecimal: ',',
        separadorMilhar: '.',
        posicaoSimbolo: 'depois',
        nome: 'Metical'
    },
    formatacao: {
        data: { formato: 'DD/MM/YYYY', separador: '/', incluirHora: false },
        numero: { separadorDecimal: ',', separadorMilhar: '.', casasDecimais: 2 }
    }
};

export default function Clientes() {
    // ==============================================
    // ESTADOS PRINCIPAIS
    // ==============================================
    const [clientes, setClientes] = useState([]);
    const [filteredClientes, setFilteredClientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCliente, setSelectedCliente] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [clienteToDelete, setClienteToDelete] = useState(null);
    const [clienteDetalhes, setClienteDetalhes] = useState(null);
    const [message, setMessage] = useState({ text: '', type: '' });
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 9,
        total: 0,
        pages: 0
    });
    const [stats, setStats] = useState({
        total: 0,
        ativos: 0,
        comTelefone: 0,
        comEmail: 0,
        comNuit: 0,
        comCidade: 0,
        comProvincia: 0,
        segmentos: {},
        niveis: {}
    });
    const [isSuperadmin, setIsSuperadmin] = useState(false);
    const [loggedUser, setLoggedUser] = useState(null);
    const [empresaId, setEmpresaId] = useState(null);
    const [empresaNome, setEmpresaNome] = useState('');

    // Estados para criação
    const [createLoading, setCreateLoading] = useState(false);
    const [createMessage, setCreateMessage] = useState({ text: '', type: '' });

    // Estados para edição
    const [editLoading, setEditLoading] = useState(false);
    const [editMessage, setEditMessage] = useState({ text: '', type: '' });

    // ==============================================
    // 🔥 ESTADOS — MÉTRICAS DINÂMICAS POR ANO
    // ==============================================
    const [metricasAno, setMetricasAno] = useState(new Date().getFullYear());
    const [metricas, setMetricas] = useState(null);
    const [evolucaoMetricas, setEvolucaoMetricas] = useState([]);
    const [loadingMetricas, setLoadingMetricas] = useState(false);
    const [erroMetricas, setErroMetricas] = useState('');

    // 🔥 CORRIGIDO: state que faltava para controlar o loading dos botões de exportação das métricas
    const [exportandoMetricas, setExportandoMetricas] = useState(null); // 'csv' | 'pdf' | null

    // ==============================================
    // 🔥 CONFIG DA EMPRESA (moeda + formatação)
    // ==============================================
    const [empresaConfig, setEmpresaConfig] = useState(CONFIG_PADRAO);

    // ==============================================
    // FILTROS AVANÇADOS
    // ==============================================
    const [filtros, setFiltros] = useState({
        segmento: 'todos',
        fidelidade: 'todos',
        status: 'todos',
        dataInicio: '',
        dataFim: ''
    });
    const [showFiltros, setShowFiltros] = useState(false);

    // ==============================================
    // FORM DATA - CRIAÇÃO
    // ==============================================
    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        telefone: '',
        nuit: '',
        endereco: '',
        cidade: '',
        provincia: '',
        segmento: 'Varejo',
        nivelFidelidade: 'Bronze',
        ativo: true
    });

    // ==============================================
    // FORM DATA - EDIÇÃO
    // ==============================================
    const [editFormData, setEditFormData] = useState(null);

    // ==============================================
    // API INSTANCE (baseURL sem /api)
    // ==============================================
    const api = useMemo(() => axios.create({
        baseURL: '/api',
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Cache-Control': 'no-cache'
        }
    }), []);

    // ==============================================
    // 🔥 FORMATAR MOEDA (DINÂMICA)
    // ==============================================
    const formatarMoeda = useMemo(() => {
        return (valor) => {
            const cfg = empresaConfig.moeda || CONFIG_PADRAO.moeda;
            const sepDec = cfg.separadorDecimal || ',';
            const sepMil = cfg.separadorMilhar || '.';
            const casasDec = cfg.casasDecimais ?? 2;
            const simbolo = cfg.simbolo || 'MT';

            if (valor === undefined || valor === null || isNaN(valor)) {
                const zeros = '0'.repeat(casasDec);
                return `0${sepDec}${zeros} ${simbolo}`;
            }

            let valorFormatado = Number(valor).toFixed(casasDec);
            valorFormatado = valorFormatado.replace('.', sepDec);

            const partes = valorFormatado.split(sepDec);
            partes[0] = partes[0].replace(/\B(?=(\d{3})+(?!\d))/g, sepMil);
            valorFormatado = partes.join(sepDec);

            return `${valorFormatado} ${simbolo}`;
        };
    }, [empresaConfig.moeda]);

    // ==============================================
    // 🔥 FORMATAR DATA (DINÂMICA)
    // ==============================================
    const formatarData = useCallback((data) => {
        if (!data) return '-';
        const d = new Date(data);
        if (isNaN(d.getTime())) return '-';

        const cfg = empresaConfig.formatacao?.data || CONFIG_PADRAO.formatacao.data;
        let formato = cfg.formato || 'DD/MM/YYYY';
        const dia = String(d.getDate()).padStart(2, '0');
        const mesNum = String(d.getMonth() + 1).padStart(2, '0');
        const anoNum = d.getFullYear();

        formato = formato.replace('DD', dia);
        formato = formato.replace('MM', mesNum);
        formato = formato.replace('YYYY', anoNum);
        formato = formato.replace('YY', String(anoNum).slice(-2));

        return formato;
    }, [empresaConfig.formatacao]);

    // ==============================================
    // 🔥 APLICAR CONFIGURAÇÃO DA EMPRESA
    // ==============================================
    const aplicarConfiguracao = useCallback((resData) => {
        if (!resData) return;
        const cfg = resData.configuracao;
        const emp = resData.empresa;

        if (cfg || emp) {
            setEmpresaConfig(prev => ({
                ...prev,
                moeda: cfg?.moeda || emp?.moeda || prev.moeda,
                formatacao: cfg?.formatacao || emp?.formatacao || prev.formatacao
            }));
        }
    }, []);

    // ==============================================
    // 🔥 FUNÇÕES AUXILIARES
    // ==============================================
    const extrairEmpresaId = (empresa) => {
        if (!empresa) return null;
        if (typeof empresa === 'string') return empresa;
        if (typeof empresa === 'object' && empresa._id) return empresa._id;
        if (typeof empresa === 'object' && empresa.id) return empresa.id;
        return null;
    };

    const obterEmpresaIdDoUtilizador = (user) => {
        if (!user) return null;
        let id = extrairEmpresaId(user.empresaId);
        if (id) return id;
        id = extrairEmpresaId(user.empresa);
        if (id) return id;
        id = extrairEmpresaId(user.empresaAtual);
        if (id) return id;
        if (user.empresas && Array.isArray(user.empresas) && user.empresas.length > 0) {
            id = extrairEmpresaId(user.empresas[0]);
            if (id) return id;
        }
        return null;
    };

    const obterEmpresaNome = (user) => {
        if (!user) return null;
        if (user.empresaId && typeof user.empresaId === 'object' && user.empresaId.nome) {
            return user.empresaId.nome;
        }
        if (user.empresaAtual && typeof user.empresaAtual === 'object' && user.empresaAtual.nome) {
            return user.empresaAtual.nome;
        }
        if (user.empresaNome) return user.empresaNome;
        if (user.empresa && typeof user.empresa === 'string') return user.empresa;
        return null;
    };

    // ==============================================
    // BADGES E ÍCONES
    // ==============================================
    const getNivelIcon = (nivel) => {
        const icons = {
            'Bronze': '🥉',
            'Prata': '🥈',
            'Ouro': '🥇',
            'Platina': '💎',
            'Diamante': '👑'
        };
        return icons[nivel] || '⭐';
    };

    const getSegmentoIcon = (segmento) => {
        const icons = {
            'Varejo': '🛒',
            'Atacado': '📦',
            'Corporativo': '🏢',
            'Premium': '⭐',
            'VIP': '👑'
        };
        return icons[segmento] || '📋';
    };

    const getNivelCor = (nivel) => {
        const cores = {
            'Bronze': '#cd7f32',
            'Prata': '#a0a0a0',
            'Ouro': '#d4a017',
            'Platina': '#a8a8a8',
            'Diamante': '#4f46e5'
        };
        return cores[nivel] || '#6b7280';
    };

    const getSegmentoCor = (segmento) => {
        const cores = {
            'Varejo': '#10b981',
            'Atacado': '#f59e0b',
            'Corporativo': '#4f46e5',
            'Premium': '#8b5cf6',
            'VIP': '#ec4899'
        };
        return cores[segmento] || '#6b7280';
    };

    // ==============================================
    // FUNÇÕES DE TOAST
    // ==============================================
    const showTempToast = (title, message, type = 'success') => {
        setMessage({ text: `${title}: ${message}`, type });
        setTimeout(() => setMessage({ text: '', type: '' }), 4000);
    };

    // ==============================================
    // BUSCAR DADOS
    // ==============================================
    const fetchClientes = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setLoading(false);
                return;
            }

            const response = await api.get('/clientes'); 

            if (response.data && response.data.success) {
                const clientesData = response.data.data || [];
                setClientes(clientesData);
                setFilteredClientes(clientesData);
                setPagination(prev => ({
                    ...prev,
                    total: clientesData.length,
                    pages: Math.ceil(clientesData.length / prev.limit)
                }));

                if (response.data.stats) {
                    setStats(response.data.stats);
                } else {
                    calcularStats(clientesData);
                }
            } else {
                const clientesData = Array.isArray(response.data) ? response.data : response.data.data || [];
                setClientes(clientesData);
                setFilteredClientes(clientesData);
                setPagination(prev => ({
                    ...prev,
                    total: clientesData.length,
                    pages: Math.ceil(clientesData.length / prev.limit)
                }));
                calcularStats(clientesData);
            }
        } catch (err) {
            console.error('❌ Erro ao carregar clientes:', err);
            showTempToast('Erro', 'Não foi possível carregar os clientes', 'danger');
        } finally {
            setLoading(false);
        }
    };

    // ==============================================
    // CALCULAR ESTATÍSTICAS (FALLBACK)
    // ==============================================
    const calcularStats = (clientesData) => {
        const newStats = {
            total: clientesData.length,
            ativos: clientesData.filter(c => c.ativo !== false).length,
            comTelefone: clientesData.filter(c => c.telefone).length,
            comEmail: clientesData.filter(c => c.email && c.email.trim()).length,
            comNuit: clientesData.filter(c => c.nuit && c.nuit.trim()).length,
            comCidade: clientesData.filter(c => c.cidade && c.cidade.trim()).length,
            comProvincia: clientesData.filter(c => c.provincia && c.provincia.trim()).length,
            segmentos: {},
            niveis: {}
        };

        clientesData.forEach(c => {
            if (c.segmento) {
                newStats.segmentos[c.segmento] = (newStats.segmentos[c.segmento] || 0) + 1;
            }
            if (c.nivelFidelidade) {
                newStats.niveis[c.nivelFidelidade] = (newStats.niveis[c.nivelFidelidade] || 0) + 1;
            }
        });

        setStats(newStats);
    };

    // ==============================================
    // BUSCAR USUÁRIO LOGADO
    // ==============================================
    const fetchLoggedUser = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    setLoggedUser(storedUser);
                    setIsSuperadmin(storedUser.perfil === 'Superadmin');
                    const empresaIdExtraido = obterEmpresaIdDoUtilizador(storedUser);
                    setEmpresaId(empresaIdExtraido);
                    setEmpresaNome(obterEmpresaNome(storedUser) || 'N/A');
                    return storedUser;
                }
                return null;
            }

            const response = await api.get('/auth/users/me'); 

            if (response.data && response.data.success) {
                const userData = response.data.data;
                setLoggedUser(userData);
                setIsSuperadmin(userData.perfil === 'Superadmin');
                const empresaIdExtraido = obterEmpresaIdDoUtilizador(userData);
                setEmpresaId(empresaIdExtraido);
                setEmpresaNome(obterEmpresaNome(userData) || 'N/A');
                localStorage.setItem('usuario', JSON.stringify(userData));
                return userData;
            }
        } catch (err) {
            console.error('❌ Erro ao buscar utilizador:', err);
        }
        return null;
    };

    // ==============================================
    // FILTRAR CLIENTES
    // ==============================================
    useEffect(() => {
        aplicarFiltros();
    }, [searchTerm, clientes, filtros]);

    const aplicarFiltros = () => {
        let resultados = [...clientes];

        if (searchTerm && searchTerm.trim() !== '') {
            const term = searchTerm.toLowerCase().trim();
            resultados = resultados.filter(cliente =>
                (cliente.nome || '').toLowerCase().includes(term) ||
                (cliente.email || '').toLowerCase().includes(term) ||
                (cliente.telefone || '').includes(term) ||
                (cliente.nuit || '').toLowerCase().includes(term) ||
                (cliente.endereco || '').toLowerCase().includes(term) ||
                (cliente.cidade || '').toLowerCase().includes(term) ||
                (cliente.provincia || '').toLowerCase().includes(term)
            );
        }

        if (filtros.segmento && filtros.segmento !== 'todos') {
            resultados = resultados.filter(c => c.segmento === filtros.segmento);
        }

        if (filtros.fidelidade && filtros.fidelidade !== 'todos') {
            resultados = resultados.filter(c => c.nivelFidelidade === filtros.fidelidade);
        }

        if (filtros.status && filtros.status !== 'todos') {
            const isAtivo = filtros.status === 'ativo';
            resultados = resultados.filter(c => (c.ativo !== false) === isAtivo);
        }

        setFilteredClientes(resultados);
        setPagination(prev => ({
            ...prev,
            total: resultados.length,
            pages: Math.ceil(resultados.length / prev.limit),
            page: 1
        }));
    };

    const limparFiltros = () => {
        setFiltros({
            segmento: 'todos',
            fidelidade: 'todos',
            status: 'todos',
            dataInicio: '',
            dataFim: ''
        });
        setSearchTerm('');
        setShowFiltros(false);
    };

    // ==============================================
    // CARREGAR DADOS INICIAIS
    // ==============================================
    useEffect(() => {
        const carregarDados = async () => {
            await fetchLoggedUser();
            await fetchClientes();
        };
        carregarDados();
    }, []);

    // ==============================================
    // HANDLE CHANGE - CRIAÇÃO
    // ==============================================
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: newValue }));
    };

    // ==============================================
    // HANDLE CHANGE - EDIÇÃO
    // ==============================================
    const handleEditChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setEditFormData(prev => ({ ...prev, [name]: newValue }));
    };

    // ==============================================
    // RESET FORM
    // ==============================================
    const resetForm = () => {
        setFormData({
            nome: '',
            email: '',
            telefone: '',
            nuit: '',
            endereco: '',
            cidade: '',
            provincia: '',
            segmento: 'Varejo',
            nivelFidelidade: 'Bronze',
            ativo: true
        });
        setCreateMessage({ text: '', type: '' });
    };

    // ==============================================
    // CRIAÇÃO
    // ==============================================
    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        setCreateLoading(true);
        setCreateMessage({ text: '', type: '' });

        if (!formData.nome.trim()) {
            setCreateMessage({ text: 'O nome do cliente é obrigatório', type: 'danger' });
            setCreateLoading(false);
            return;
        }
        if (!formData.email.trim()) {
            setCreateMessage({ text: 'O email é obrigatório', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setCreateMessage({ text: 'Email inválido', type: 'danger' });
            setCreateLoading(false);
            return;
        }

        let empresaParaEnviar = empresaId;
        if (!empresaParaEnviar && loggedUser) {
            empresaParaEnviar = obterEmpresaIdDoUtilizador(loggedUser);
        }
        if (!empresaParaEnviar) {
            try {
                const storedUser = JSON.parse(localStorage.getItem('usuario') || '{}');
                if (storedUser && storedUser._id) {
                    empresaParaEnviar = obterEmpresaIdDoUtilizador(storedUser);
                }
            } catch (e) {
                console.error('❌ Erro ao ler localStorage:', e);
            }
        }

        if (!empresaParaEnviar) {
            setCreateMessage({
                text: 'Utilizador sem empresa associada. Contacte o administrador.',
                type: 'danger'
            });
            setCreateLoading(false);
            return;
        }

        const dadosParaEnviar = {
            nome: formData.nome.trim(),
            email: formData.email.trim(),
            telefone: formData.telefone?.trim() || '',
            nuit: formData.nuit?.trim() || '',
            endereco: formData.endereco?.trim() || '',
            cidade: formData.cidade?.trim() || '',
            provincia: formData.provincia?.trim() || '',
            segmento: formData.segmento || 'Varejo',
            nivelFidelidade: formData.nivelFidelidade || 'Bronze',
            empresaId: empresaParaEnviar,
            ativo: formData.ativo !== undefined ? formData.ativo : true
        };

        try {
            const token = localStorage.getItem('token');
            if (!token) {
                setCreateMessage({ text: 'Sessão expirada. Faça login novamente.', type: 'danger' });
                setCreateLoading(false);
                return;
            }

            const response = await api.post('/clientes', dadosParaEnviar); 
            if (response.data) {
                setCreateMessage({ text: 'Cliente criado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowCreateModal(false);
                    resetForm();
                    fetchClientes();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao criar cliente:', err);
            setCreateMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao criar cliente',
                type: 'danger'
            });
        } finally {
            setCreateLoading(false);
        }
    };

    // ==============================================
    // 🔥 CARREGAR CLIENTE PARA EDIÇÃO
    // ==============================================
    const loadClienteForEdit = async (cliente) => {
        setEditLoading(true);
        try {
            setShowDetailsModal(false);
            setClienteDetalhes(null);

            const response = await api.get(`/clientes/${cliente._id}`); 
            const clienteCompleto = response.data.data || cliente;

            setEditFormData({
                _id: clienteCompleto._id,
                nome: clienteCompleto.nome || '',
                email: clienteCompleto.email || '',
                telefone: clienteCompleto.telefone || '',
                nuit: clienteCompleto.nuit || '',
                endereco: clienteCompleto.endereco || '',
                cidade: clienteCompleto.cidade || '',
                provincia: clienteCompleto.provincia || '',
                segmento: clienteCompleto.segmento || 'Varejo',
                nivelFidelidade: clienteCompleto.nivelFidelidade || 'Bronze',
                ativo: clienteCompleto.ativo !== undefined ? clienteCompleto.ativo : true
            });

            setTimeout(() => setShowEditModal(true), 100);
        } catch (error) {
            console.error('Erro ao carregar cliente:', error);
            setEditMessage({ text: 'Erro ao carregar dados do cliente', type: 'danger' });
            setShowEditModal(false);
        } finally {
            setEditLoading(false);
        }
    };

    const handleEdit = (cliente) => {
        setSelectedCliente(cliente);
        loadClienteForEdit(cliente);
    };

    // ==============================================
    // EDIÇÃO
    // ==============================================
    const handleEditSubmit = async (e) => {
        e.preventDefault();
        setEditLoading(true);
        setEditMessage({ text: '', type: '' });

        if (!editFormData.nome.trim()) {
            setEditMessage({ text: 'O nome do cliente é obrigatório', type: 'danger' });
            setEditLoading(false);
            return;
        }
        if (!editFormData.email.trim()) {
            setEditMessage({ text: 'O email é obrigatório', type: 'danger' });
            setEditLoading(false);
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(editFormData.email)) {
            setEditMessage({ text: 'Email inválido', type: 'danger' });
            setEditLoading(false);
            return;
        }

        try {
            const { _id, ...updateData } = editFormData;
            const response = await api.put(`/clientes/${_id}`, updateData); 

            if (response.data) {
                setEditMessage({ text: 'Cliente atualizado com sucesso!', type: 'success' });
                setTimeout(() => {
                    setShowEditModal(false);
                    setEditFormData(null);
                    setSelectedCliente(null);
                    fetchClientes();
                }, 1500);
            }
        } catch (err) {
            console.error('❌ Erro ao atualizar cliente:', err);
            setEditMessage({
                text: err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao atualizar cliente',
                type: 'danger'
            });
        } finally {
            setEditLoading(false);
        }
    };

    // ==============================================
    // 🔥 CARREGAR MÉTRICAS DO CLIENTE POR ANO — CORRIGIDO
    // 🔥 USA `api.get` (baseURL = /api)
    // ==============================================
    const carregarMetricas = useCallback(async (clienteId, ano) => {
        setLoadingMetricas(true);
        setErroMetricas('');
        try {
            // ✅ CORRIGIDO: usa a instância `api` (sem /api)
            const res = await api.get(`/clientes/${clienteId}/metricas`, { 
                params: { ano }
            });

            if (res.data?.success) {
                setMetricas(res.data.metricas || null);
                setEvolucaoMetricas(res.data.evolucao || []);
            } else {
                setMetricas(null);
                setErroMetricas('Não foi possível carregar as métricas.');
            }
        } catch (err) {
            console.error('❌ Erro ao carregar métricas:', err);
            setMetricas(null);
            setErroMetricas(err.response?.data?.erro || 'Erro ao carregar métricas.');
        } finally {
            setLoadingMetricas(false);
        }
    }, [api]);

    // ==============================================
    // 🔥 HANDLER: MUDAR ANO DAS MÉTRICAS
    // ==============================================
    const handleMetricasAnoChange = (novoAno) => {
        setMetricasAno(novoAno);
        if (clienteDetalhes?._id) {
            carregarMetricas(clienteDetalhes._id, novoAno);
        }
    };

    // ==============================================
    // 🔥 VER DETALHES (com métricas dinâmicas)
    // ==============================================
    const verDetalhes = async (cliente) => {
        try {
            setShowEditModal(false);
            setEditFormData(null);

            const response = await api.get(`/clientes/${cliente._id}`); 
            const clienteCompleto = response.data.data || cliente;

            setClienteDetalhes(clienteCompleto);
            setMetricas(null);
            setEvolucaoMetricas([]);
            setErroMetricas('');

            const anoAtual = new Date().getFullYear();
            setMetricasAno(anoAtual);

            setTimeout(() => setShowDetailsModal(true), 100);

            // Carrega métricas do ano atual
            carregarMetricas(cliente._id, anoAtual);
        } catch (err) {
            console.error('❌ Erro ao buscar detalhes:', err);
            setClienteDetalhes(cliente);
            setShowDetailsModal(true);
            const anoAtual = new Date().getFullYear();
            setMetricasAno(anoAtual);
            carregarMetricas(cliente._id, anoAtual);
        }
    };

    // ==============================================
    // AÇÕES RÁPIDAS
    // ==============================================
    const abrirWhatsApp = (cliente) => {
        const numero = cliente.telefone?.replace(/\D/g, '');
        if (numero) {
            window.open(`https://wa.me/${numero}`, '_blank');
        } else {
            showTempToast('Aviso', 'Cliente sem número de telefone', 'warning');
        }
    };

    const abrirEmail = (cliente) => {
        if (cliente.email) {
            window.open(`mailto:${cliente.email}`, '_blank');
        } else {
            showTempToast('Aviso', 'Cliente sem email', 'warning');
        }
    };


    // ==============================================
    // 🔥 TOOLTIP CUSTOMIZADA (igual ao DashboardVendas)
    // ==============================================
    function CustomTooltipCliente({ active, payload, label, formatValue }) {
        if (!active || !payload || !payload.length) return null;
        return (
            <div className="custom-tooltip-cliente">
                {label && <div className="custom-tooltip-label">{label}</div>}
                {payload.map((entry, i) => (
                    <div key={i} className="custom-tooltip-item">
                        <span
                            className="custom-tooltip-dot"
                            style={{ background: entry.color || entry.payload?.cor || '#4f46e5' }}
                        />
                        <span className="custom-tooltip-name">{entry.name}:</span>
                        <span className="custom-tooltip-value">
                            {formatValue ? formatValue(entry.value) : entry.value}
                        </span>
                    </div>
                ))}
            </div>
        );
    }


    // ==============================================
    // EXPORTAÇÃO
    // ==============================================
    const exportarClientes = async (formato) => {
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada. Faça login novamente.', 'danger');
                return;
            }

            const params = new URLSearchParams();
            params.append('formato', formato);

            if (searchTerm) params.append('search', searchTerm);
            if (filtros.segmento && filtros.segmento !== 'todos') params.append('segmento', filtros.segmento);
            if (filtros.fidelidade && filtros.fidelidade !== 'todos') params.append('fidelidade', filtros.fidelidade);
            if (filtros.status && filtros.status !== 'todos') params.append('status', filtros.status);

            const url = `/api/clientes/exportar?${params.toString()}`; 
            console.log('📤 Exportando clientes com filtros:', params.toString());

            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.erro || 'Erro na exportação');
            }

            const blob = await response.blob();
            const extensao = formato === 'csv' ? 'csv' : 'xlsx';

            const urlDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlDownload;
            link.download = `clientes_${new Date().toISOString().split('T')[0]}.${extensao}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlDownload);

            showTempToast('Sucesso', `Clientes exportados em ${formato.toUpperCase()}`, 'success');
        } catch (err) {
            console.error('❌ Erro na exportação:', err);
            showTempToast('Erro', err.message || 'Falha na exportação', 'danger');
        }
    };

    // ==============================================
    // 🔥 CORRIGIDO: EXPORTAR MÉTRICAS DO CLIENTE (CSV / PDF)
    // ==============================================
    const exportarMetricasCliente = async (formato) => {
        if (!clienteDetalhes?._id) {
            showTempToast('Erro', 'Nenhum cliente selecionado', 'danger');
            return;
        }

        setExportandoMetricas(formato);
        try {
            const token = localStorage.getItem('token');
            if (!token) {
                showTempToast('Erro', 'Sessão expirada. Faça login novamente.', 'danger');
                return;
            }

            const url = `/api/clientes/${clienteDetalhes._id}/metricas/exportar?formato=${formato}&ano=${metricasAno}`; 
            console.log('📤 Exportando métricas do cliente:', url);

            const response = await fetch(url, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (!response.ok) {
                let erroMsg = 'Erro na exportação das métricas';
                try {
                    const errorData = await response.json();
                    erroMsg = errorData.erro || erroMsg;
                } catch (_) { /* resposta não é JSON */ }
                throw new Error(erroMsg);
            }

            const blob = await response.blob();
            const extensao = formato === 'pdf' ? 'pdf' : 'csv';

            const urlDownload = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = urlDownload;
            link.download = `metricas_${(clienteDetalhes.nome || 'cliente').replace(/\s+/g, '_')}_${metricasAno}.${extensao}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(urlDownload);

            showTempToast('Sucesso', `Métricas exportadas em ${formato.toUpperCase()}`, 'success');
        } catch (err) {
            console.error('❌ Erro ao exportar métricas:', err);
            showTempToast('Erro', err.message || 'Falha na exportação', 'danger');
        } finally {
            setExportandoMetricas(null);
        }
    };

    // ==============================================
    // EXCLUSÃO
    // ==============================================
    const openDeleteModal = (cliente) => {
        setClienteToDelete(cliente);
        setShowDeleteModal(true);
    };

    const closeDeleteModal = () => {
        setShowDeleteModal(false);
        setClienteToDelete(null);
    };

    const confirmDelete = async () => {
        if (!clienteToDelete) return;

        try {
            const response = await api.delete(`/clientes/${clienteToDelete._id}`); 
            if (response.data) {
                showTempToast('Sucesso', `Cliente "${clienteToDelete.nome}" removido com sucesso!`, 'success');
                closeDeleteModal();
                fetchClientes();
            }
        } catch (err) {
            console.error('❌ Erro ao remover cliente:', err);
            showTempToast('Erro', err.response?.data?.erro || err.response?.data?.mensagem || 'Erro ao remover cliente', 'danger');
        }
    };

    // ==============================================
    // PAGINAÇÃO
    // ==============================================
    const getCurrentPageClientes = () => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        const endIndex = startIndex + pagination.limit;
        return filteredClientes.slice(startIndex, endIndex);
    };

    const goToPage = (page) => {
        if (page < 1 || page > pagination.pages) return;
        setPagination(prev => ({ ...prev, page }));
    };

    const renderPagination = () => {
        const { page, pages, total } = pagination;
        if (pages <= 1) return null;

        const startItem = (page - 1) * pagination.limit + 1;
        const endItem = Math.min(page * pagination.limit, total);

        return (
            <div className="clientes-pagination">
                <div className="pagination-info">
                    <span>Mostrando {startItem} - {endItem} de {total} clientes</span>
                </div>
                <div className="pagination-controls">
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page - 1)}
                        disabled={page === 1}
                    >
                        <Icon name="chevron_left" size={18} />
                    </button>
                    <div className="pagination-numbers">
                        {Array.from({ length: pages }, (_, i) => i + 1).map(p => (
                            <button
                                key={p}
                                className={`pagination-number ${page === p ? 'active' : ''}`}
                                onClick={() => goToPage(p)}
                            >
                                {p}
                            </button>
                        ))}
                    </div>
                    <button
                        className="pagination-btn"
                        onClick={() => goToPage(page + 1)}
                        disabled={page === pages}
                    >
                        <Icon name="chevron_right" size={18} />
                    </button>
                </div>
            </div>
        );
    };

    // ==============================================
    // RENDER PRINCIPAL
    // ==============================================
    const currentClientes = getCurrentPageClientes();
    const anosDisponiveis = Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i);

    return (
        <div className="clientes-container">
            

            {/* ==============================================
    MODAL DE CRIAÇÃO
    ============================================== */}
{showCreateModal && (
    <div className="cliente-modal-overlay" onClick={() => { setShowCreateModal(false); resetForm(); }}>
        <div className="cliente-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cliente-modal-header">
                <h3><Icon name="person_add" size={22} style={{ color: '#4F46E5' }} /> Novo Cliente</h3>
                <button className="cliente-modal-close" onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>
            <div className="cliente-modal-body">
                {createMessage.text && (
                    <div className={`cliente-modal-alert cliente-modal-alert-${createMessage.type}`}>
                        <Icon name={createMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{createMessage.text}</span>
                        <button className="cliente-alert-close" onClick={() => setCreateMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}
                <form onSubmit={handleCreateSubmit}>
                    <div className="cliente-modal-section">
                        <div className="cliente-form-group">
                            <label>Nome <span className="cliente-required">*</span></label>
                            <input type="text" name="nome" value={formData.nome} onChange={handleChange} required placeholder="Nome completo" />
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>Email <span className="cliente-required">*</span></label>
                                <input type="email" name="email" value={formData.email} onChange={handleChange} required placeholder="cliente@email.com" />
                            </div>
                            <div className="cliente-form-group">
                                <label>Telefone</label>
                                <input type="tel" name="telefone" value={formData.telefone} onChange={handleChange} placeholder="+258 84 123 4567" />
                            </div>
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>NUIT</label>
                                <input type="text" name="nuit" value={formData.nuit} onChange={handleChange} placeholder="NUIT" />
                            </div>
                            <div className="cliente-form-group">
                                <label>Endereço</label>
                                <input type="text" name="endereco" value={formData.endereco} onChange={handleChange} placeholder="Endereço completo" />
                            </div>
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>Cidade</label>
                                <input type="text" name="cidade" value={formData.cidade} onChange={handleChange} placeholder="Cidade" />
                            </div>
                            <div className="cliente-form-group">
                                <label>Província</label>
                                <input type="text" name="provincia" value={formData.provincia} onChange={handleChange} placeholder="Província" />
                            </div>
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>Segmento</label>
                                <select
                                    name="segmento"
                                    className="cliente-select-segmento"
                                    value={formData.segmento}
                                    onChange={handleChange}
                                >
                                    <option value="Varejo">🛒 Varejo</option>
                                    <option value="Atacado">📦 Atacado</option>
                                    <option value="Corporativo">🏢 Corporativo</option>
                                    <option value="Premium">⭐ Premium</option>
                                    <option value="VIP">👑 VIP</option>
                                </select>
                            </div>
                            <div className="cliente-form-group">
                                <label>Nível de Fidelidade</label>
                                <select
                                    name="nivelFidelidade"
                                    className="cliente-select-nivel-fidelidade"
                                    value={formData.nivelFidelidade}
                                    onChange={handleChange}
                                >
                                    <option value="Bronze">🥉 Bronze</option>
                                    <option value="Prata">🥈 Prata</option>
                                    <option value="Ouro">🥇 Ouro</option>
                                    <option value="Platina">💎 Platina</option>
                                    <option value="Diamante">👑 Diamante</option>
                                </select>
                            </div>
                        </div>
                        <div className="cliente-form-group cliente-checkbox-group">
                            <label className="cliente-checkbox-label">
                                <input
                                    type="checkbox"
                                    name="ativo"
                                    checked={formData.ativo !== undefined ? formData.ativo : true}
                                    onChange={handleChange}
                                />
                                <span>Cliente Ativo</span>
                            </label>
                        </div>
                    </div>
                    <div className="cliente-modal-actions">
                        <button type="submit" className="cliente-modal-btn-primary" disabled={createLoading}>
                            {createLoading ? 'A criar...' : 'Criar Cliente'}
                        </button>
                        <button type="button" className="cliente-modal-btn-secondary" onClick={resetForm}>Limpar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* ==============================================
    MODAL DE EDIÇÃO
    ============================================== */}
{showEditModal && editFormData && (
    <div className="cliente-modal-overlay" onClick={() => setShowEditModal(false)}>
        <div className="cliente-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="cliente-modal-header">
                <h3><Icon name="edit" size={22} /> Editar Cliente</h3>
                <button className="cliente-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="cliente-modal-body">
                {editMessage.text && (
                    <div className={`cliente-modal-alert cliente-modal-alert-${editMessage.type}`}>
                        <Icon name={editMessage.type === 'success' ? 'check_circle' : 'error'} size={18} />
                        <span>{editMessage.text}</span>
                        <button className="cliente-alert-close" onClick={() => setEditMessage({ text: '', type: '' })}>×</button>
                    </div>
                )}
                <form onSubmit={handleEditSubmit}>
                    <div className="cliente-modal-section">
                        <div className="cliente-form-group">
                            <label>Nome <span className="cliente-required">*</span></label>
                            <input type="text" name="nome" value={editFormData.nome} onChange={handleEditChange} required placeholder="Nome completo" />
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>Email <span className="cliente-required">*</span></label>
                                <input type="email" name="email" value={editFormData.email} onChange={handleEditChange} required placeholder="cliente@email.com" />
                            </div>
                            <div className="cliente-form-group">
                                <label>Telefone</label>
                                <input type="tel" name="telefone" value={editFormData.telefone} onChange={handleEditChange} placeholder="+258 84 123 4567" />
                            </div>
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>NUIT</label>
                                <input type="text" name="nuit" value={editFormData.nuit} onChange={handleEditChange} placeholder="NUIT" />
                            </div>
                            <div className="cliente-form-group">
                                <label>Endereço</label>
                                <input type="text" name="endereco" value={editFormData.endereco} onChange={handleEditChange} placeholder="Endereço completo" />
                            </div>
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>Cidade</label>
                                <input type="text" name="cidade" value={editFormData.cidade} onChange={handleEditChange} placeholder="Cidade" />
                            </div>
                            <div className="cliente-form-group">
                                <label>Província</label>
                                <input type="text" name="provincia" value={editFormData.provincia} onChange={handleEditChange} placeholder="Província" />
                            </div>
                        </div>
                        <div className="cliente-form-row">
                            <div className="cliente-form-group">
                                <label>Segmento</label>
                                <select name="segmento" value={editFormData.segmento} onChange={handleEditChange}>
                                    <option value="Varejo">🛒 Varejo</option>
                                    <option value="Atacado">📦 Atacado</option>
                                    <option value="Corporativo">🏢 Corporativo</option>
                                    <option value="Premium">⭐ Premium</option>
                                    <option value="VIP">👑 VIP</option>
                                </select>
                            </div>
                            <div className="cliente-form-group">
                                <label>Nível de Fidelidade</label>
                                <select name="nivelFidelidade" value={editFormData.nivelFidelidade} onChange={handleEditChange}>
                                    <option value="Bronze">🥉 Bronze</option>
                                    <option value="Prata">🥈 Prata</option>
                                    <option value="Ouro">🥇 Ouro</option>
                                    <option value="Platina">💎 Platina</option>
                                    <option value="Diamante">👑 Diamante</option>
                                </select>
                            </div>
                        </div>
                        <div className="cliente-form-group cliente-checkbox-group">
                            <label className="cliente-checkbox-label">
                                <input
                                    type="checkbox"
                                    name="ativo"
                                    checked={editFormData.ativo !== undefined ? editFormData.ativo : true}
                                    onChange={handleEditChange}
                                />
                                <span>Cliente Ativo</span>
                            </label>
                        </div>
                    </div>
                    <div className="cliente-modal-actions">
                        <button type="submit" className="cliente-modal-btn-primary" disabled={editLoading}>
                            {editLoading ? 'A atualizar...' : 'Salvar Alterações'}
                        </button>
                        <button type="button" className="cliente-modal-btn-secondary" onClick={() => setShowEditModal(false)}>Cancelar</button>
                    </div>
                </form>
            </div>
        </div>
    </div>
)}

{/* ==============================================
    MODAL DE DETALHES — COM MÉTRICAS + GRÁFICOS + EXPORTAÇÃO
    ============================================== */}
{showDetailsModal && clienteDetalhes && (
    <div className="cliente-modal-overlay" onClick={() => setShowDetailsModal(false)}>
        <div className="cliente-modal-content cliente-modal-large" onClick={(e) => e.stopPropagation()}>

            {/* ===== HEADER COM BOTÕES DE EXPORTAÇÃO ===== */}
            <div className="cliente-modal-header">
                <h3>
                    <Icon name="person" size={22} />
                    {clienteDetalhes.nome}
                </h3>

                <div className="cliente-modal-header-actions">
                    {/* 🔥 BOTÃO EXPORTAR CSV */}
                    <button
                        className="cliente-btn-export"
                        onClick={() => exportarMetricasCliente('csv')}
                        disabled={exportandoMetricas || loadingMetricas}
                        title="Exportar métricas em CSV"
                    >
                        {exportandoMetricas === 'csv' ? (
                            <>
                                <div className="cliente-spinner-small" />
                                <span>A exportar...</span>
                            </>
                        ) : (
                            <>
                                <Icon name="file_download" size={14} />
                                <span>CSV</span>
                            </>
                        )}
                    </button>

                    {/* 🔥 BOTÃO EXPORTAR PDF */}
                    <button
                        className="cliente-btn-export cliente-btn-export-pdf"
                        onClick={() => exportarMetricasCliente('pdf')}
                        disabled={exportandoMetricas || loadingMetricas}
                        title="Exportar métricas em PDF"
                    >
                        {exportandoMetricas === 'pdf' ? (
                            <>
                                <div className="cliente-spinner-small" />
                                <span>A gerar...</span>
                            </>
                        ) : (
                            <>
                                <Icon name="picture_as_pdf" size={14} />
                                <span>PDF</span>
                            </>
                        )}
                    </button>

                    <button className="cliente-modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
                </div>
            </div>

            <div className="cliente-modal-body cliente-modal-body-detalhes">
                {/* 🔥 INFO BASE */}
                <div className="cliente-detalhes-grid">
                    <div className="cliente-info-item">
                        <span className="cliente-label">📧 Email</span>
                        <span className="cliente-value">{clienteDetalhes.email}</span>
                    </div>
                    <div className="cliente-info-item">
                        <span className="cliente-label">📞 Telefone</span>
                        <span className="cliente-value">{clienteDetalhes.telefone || '-'}</span>
                    </div>
                    <div className="cliente-info-item">
                        <span className="cliente-label">📍 Localização</span>
                        <span className="cliente-value">
                            {[clienteDetalhes.cidade, clienteDetalhes.provincia].filter(Boolean).join(', ') || '-'}
                        </span>
                    </div>
                    <div className="cliente-info-item">
                        <span className="cliente-label">📊 Status</span>
                        <span className="cliente-value">
                            <span className={`cliente-status-badge ${clienteDetalhes.ativo !== false ? 'cliente-status-active' : 'cliente-status-inactive'}`}>
                                {clienteDetalhes.ativo !== false ? '🟢 Ativo' : '🔴 Inativo'}
                            </span>
                        </span>
                    </div>
                    <div className="cliente-info-item">
                        <span className="cliente-label">🏆 Nível</span>
                        <span className="cliente-value" style={{ color: getNivelCor(clienteDetalhes.nivelFidelidade) }}>
                            {getNivelIcon(clienteDetalhes.nivelFidelidade)} {clienteDetalhes.nivelFidelidade || '-'}
                        </span>
                    </div>
                    <div className="cliente-info-item">
                        <span className="cliente-label">🏷️ Segmento</span>
                        <span className="cliente-value" style={{ color: getSegmentoCor(clienteDetalhes.segmento) }}>
                            {getSegmentoIcon(clienteDetalhes.segmento)} {clienteDetalhes.segmento || '-'}
                        </span>
                    </div>
                </div>

                {/* 🔥 MÉTRICAS COM FILTRO DE ANO */}
                <div className="cliente-metricas">
                    <div className="cliente-metricas-header">
                        <h4>
                            <Icon name="chart" size={20} color="currentColor" />
                            Métricas
                        </h4>
                        <div className="cliente-metricas-filtro-ano">
                            <label>Ano</label>
                            <select
                                value={metricasAno}
                                onChange={(e) => handleMetricasAnoChange(parseInt(e.target.value))}
                                disabled={loadingMetricas}
                            >
                                {anosDisponiveis.map(a => (
                                    <option key={a} value={a}>{a}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {loadingMetricas ? (
                        <div className="cliente-metricas-loading">
                            <div className="cliente-spinner-small"></div>
                            <span>A carregar métricas de {metricasAno}...</span>
                        </div>
                    ) : erroMetricas ? (
                        <div className="cliente-metricas-erro">
                            <Icon name="error" size={16} color="#dc2626" />
                            <span>{erroMetricas}</span>
                        </div>
                    ) : (
                        <>
                            {/* KPIs */}
                            <div className="cliente-metricas-grid">
                                <div className="cliente-metrica-card">
                                    <span className="cliente-metrica-valor">
                                        {metricas?.totalCompras ?? 0}
                                    </span>
                                    <span className="cliente-metrica-label">Compras</span>
                                </div>
                                <div className="cliente-metrica-card">
                                    <span className="cliente-metrica-valor">
                                        {formatarMoeda(metricas?.totalFaturado ?? 0)}
                                    </span>
                                    <span className="cliente-metrica-label">Total Gasto</span>
                                </div>
                                <div className="cliente-metrica-card">
                                    <span className="cliente-metrica-valor">
                                        {formatarMoeda(metricas?.mediaGasto ?? 0)}
                                    </span>
                                    <span className="cliente-metrica-label">Média/Compra</span>
                                </div>
                                <div className="cliente-metrica-card">
                                    <span className="cliente-metrica-valor">
                                        {metricas?.ultimaCompra
                                            ? formatarData(metricas.ultimaCompra)
                                            : '-'}
                                    </span>
                                    <span className="cliente-metrica-label">Última Compra</span>
                                </div>
                            </div>

                            {/* LINHA EXTRA */}
                            <div className="cliente-metricas-extra">
                                <div className="cliente-metrica-extra-item">
                                    <span className="cliente-label">💰 Em Dívida</span>
                                    <strong style={{ color: (metricas?.emDivida ?? 0) > 0 ? '#dc2626' : '#10b981' }}>
                                        {formatarMoeda(metricas?.emDivida ?? 0)}
                                    </strong>
                                </div>
                                <div className="cliente-metrica-extra-item">
                                    <span className="cliente-label">⏳ Sem comprar há</span>
                                    <strong>
                                        {metricas?.diasDesdeUltimaCompra != null
                                            ? `${metricas.diasDesdeUltimaCompra} dias`
                                            : '—'}
                                    </strong>
                                </div>
                                <div className="cliente-metrica-extra-item">
                                    <span className="cliente-label">🧾 Recibos</span>
                                    <strong>{metricas?.numeroRecibos ?? 0}</strong>
                                </div>
                                <div className="cliente-metrica-extra-item">
                                    <span className="cliente-label">⚠️ Faturas vencidas</span>
                                    <strong style={{ color: (metricas?.faturasVencidas ?? 0) > 0 ? '#dc2626' : '#10b981' }}>
                                        {metricas?.faturasVencidas ?? 0}
                                    </strong>
                                </div>
                            </div>

                            {/* ============================================= */}
                            {/* 🔥 GRÁFICO 1: EVOLUÇÃO MENSAL (ÁREA) */}
                            {/* ============================================= */}
                            {evolucaoMetricas.length > 0 && (
                                <div className="cliente-grafico-card">
                                    <h4 className="cliente-grafico-titulo">
                                        <Icon name="show_chart" size={16} color="#4f46e5" />
                                        Evolução Mensal ({metricasAno})
                                    </h4>

                                    {!evolucaoMetricas.some(m => (m.totalFaturado || 0) > 0) ? (
                                        <p className="cliente-grafico-vazio">
                                            Sem movimento em {metricasAno}
                                        </p>
                                    ) : (
                                        <ResponsiveContainer width="100%" height={220}>
                                            <AreaChart
                                                data={evolucaoMetricas}
                                                margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                                            >
                                                <defs>
                                                    <linearGradient id="gradClienteFat" x1="0" y1="0" x2="0" y2="1">
                                                        <stop offset="5%" stopColor="#4f46e5" stopOpacity={0.7} />
                                                        <stop offset="95%" stopColor="#4f46e5" stopOpacity={0.05} />
                                                    </linearGradient>
                                                </defs>
                                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                                <XAxis
                                                    dataKey="mes"
                                                    tick={{ fontSize: 11, fill: '#6b7280' }}
                                                    tickLine={false}
                                                    axisLine={false}
                                                />
                                                <YAxis
                                                    tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                                                    tick={{ fontSize: 11, fill: '#6b7280' }}
                                                    tickLine={false}
                                                    axisLine={false}
                                                    width={40}
                                                />
                                                <Tooltip
                                                    content={
                                                        <CustomTooltipCliente formatValue={formatarMoeda} />
                                                    }
                                                />
                                                <Area
                                                    type="monotone"
                                                    dataKey="totalFaturado"
                                                    stroke="#4f46e5"
                                                    fill="url(#gradClienteFat)"
                                                    strokeWidth={2}
                                                    name="Faturado"
                                                />
                                            </AreaChart>
                                        </ResponsiveContainer>
                                    )}
                                </div>
                            )}

                            {/* ============================================= */}
                            {/* 🔥 GRÁFICO 2: TOP MESES (PIZZA) */}
                            {/* ============================================= */}
                            {evolucaoMetricas.some(m => (m.totalFaturado || 0) > 0) && (
                                <div className="cliente-grafico-card">
                                    <h4 className="cliente-grafico-titulo">
                                        <Icon name="pie_chart" size={16} color="#4f46e5" />
                                        Distribuição por Mês
                                    </h4>
                                    <ResponsiveContainer width="100%" height={220}>
                                        <PieChart>
                                            <Pie
                                                data={evolucaoMetricas.filter(m => (m.totalFaturado || 0) > 0)}
                                                dataKey="totalFaturado"
                                                nameKey="mes"
                                                cx="50%"
                                                cy="50%"
                                                outerRadius="75%"
                                                innerRadius="40%"
                                                paddingAngle={2}
                                                labelLine={false}
                                                label={({ percent }) => {
                                                    if (percent < 0.05) return null;
                                                    return `${(percent * 100).toFixed(0)}%`;
                                                }}
                                            >
                                                {evolucaoMetricas
                                                    .filter(m => (m.totalFaturado || 0) > 0)
                                                    .map((_, i) => (
                                                        <Cell
                                                            key={i}
                                                            fill={[
                                                                '#4f46e5', '#10b981', '#f59e0b', '#ef4444',
                                                                '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6',
                                                                '#f97316', '#6366f1', '#84cc16', '#a855f7'
                                                            ][i % 12]}
                                                            stroke="#fff"
                                                            strokeWidth={2}
                                                        />
                                                    ))}
                                            </Pie>
                                            <Tooltip
                                                content={
                                                    <CustomTooltipCliente formatValue={formatarMoeda} />
                                                }
                                            />
                                            <Legend
                                                verticalAlign="bottom"
                                                height={30}
                                                iconType="circle"
                                                iconSize={8}
                                                wrapperStyle={{ fontSize: '11px', paddingTop: '4px' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {/* ============================================= */}
                            {/* 🔥 GRÁFICO 3: Nº DE COMPRAS (BAR) */}
                            {/* ============================================= */}
                            {evolucaoMetricas.some(m => (m.numeroCompras || 0) > 0) && (
                                <div className="cliente-grafico-card">
                                    <h4 className="cliente-grafico-titulo">
                                        <Icon name="bar_chart" size={16} color="#4f46e5" />
                                        Nº de Compras por Mês
                                    </h4>
                                    <ResponsiveContainer width="100%" height={200}>
                                        <BarChart
                                            data={evolucaoMetricas}
                                            margin={{ top: 10, right: 10, bottom: 0, left: 0 }}
                                        >
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                            <XAxis
                                                dataKey="mes"
                                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                                tickLine={false}
                                                axisLine={false}
                                            />
                                            <YAxis
                                                allowDecimals={false}
                                                tick={{ fontSize: 11, fill: '#6b7280' }}
                                                tickLine={false}
                                                axisLine={false}
                                                width={30}
                                            />
                                            <Tooltip
                                                cursor={{ fill: 'rgba(79, 70, 229, 0.05)' }}
                                                content={<CustomTooltipCliente />}
                                            />
                                            <Bar
                                                dataKey="numeroCompras"
                                                fill="#10b981"
                                                radius={[6, 6, 0, 0]}
                                                name="Compras"
                                                maxBarSize={40}
                                            />
                                        </BarChart>
                                    </ResponsiveContainer>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            <div className="cliente-modal-footer">
                <button className="cliente-btn-primary" onClick={() => handleEdit(clienteDetalhes)}>
                    <Icon name="edit" size={16} /> Editar
                </button>
                <button className="cliente-btn-secondary" onClick={() => setShowDetailsModal(false)}>Fechar</button>
            </div>
        </div>
    </div>
)}

{/* ==============================================
    MODAL DE CONFIRMAÇÃO DE EXCLUSÃO
    ============================================== */}
{showDeleteModal && clienteToDelete && (
    <div className="cliente-modal-overlay" onClick={closeDeleteModal}>
        <div className="cliente-modal-content-popups cliente-modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="cliente-modal-header">
                <h3><span className="cliente-header-icon">⚠️</span> Confirmar Exclusão</h3>
                <button className="cliente-modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="cliente-modal-body-exc">
                <div className="cliente-confirm-icon warning">
                    <Icon name="warning" size={32} />
                </div>
                <h4>Remover cliente?</h4>
                <p>
                    Tem certeza que deseja remover o cliente <strong>{clienteToDelete.nome}</strong>?
                </p>
                <p className="cliente-confirm-subtext">
                    <Icon name="info" size={14} /> Esta ação é irreversível.
                </p>
            </div>
            <div className="cliente-modal-actions">
                <button className="cliente-btn-confirm-danger" onClick={confirmDelete}>
                    <Icon name="delete_forever" size={16} /> Eliminar
                </button>
                <button className="cliente-btn-cancel" onClick={closeDeleteModal}>Cancelar</button>
            </div>
        </div>
    </div>
)}

            {/* ==============================================
                HEADER
                ============================================== */}
            <div className="clientes-header">
                <div className="header-title">
                    <h2><Icon name="people" size={24} /> Gestão de Clientes</h2>
                    <p>Gerencie os clientes para prestação de serviços</p>
                </div>
                <div className="header-actions">
                    <button className="btn-export" onClick={() => exportarClientes('excel')} title="Exportar Excel">
                        <Icon name="file_download" size={18} /> Excel
                    </button>
                    <button className="btn-export" onClick={() => exportarClientes('csv')} title="Exportar CSV">
                        <Icon name="file_download" size={18} /> CSV
                    </button>
                    <button className="btn-create" onClick={() => setShowCreateModal(true)}>
                        <Icon name="person_add" size={18} /> Novo Cliente
                    </button>
                </div>
            </div>

            {/* ==============================================
                MENSAGENS
                ============================================== */}
            {message.text && (
                <div className={`alert alert-${message.type}`}>
                    <Icon name={message.type === 'success' ? 'check_circle' : 'error'} size={16} />
                    <span>{message.text}</span>
                    <button className="alert-close" onClick={() => setMessage({ text: '', type: '' })}>×</button>
                </div>
            )}

            {/* ==============================================
                KPIs
                ============================================== */}
            <div className="clientes-kpi-grid">
                <div className="kpi-card primary">
                    <div className="kpi-icon"><Icon name="people" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.total}</span>
                        <span className="kpi-label">Total Clientes</span>
                    </div>
                </div>
                <div className="kpi-card success">
                    <div className="kpi-icon"><Icon name="check_circle" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.ativos}</span>
                        <span className="kpi-label">Ativos</span>
                    </div>
                    <span className="kpi-change positive">{stats.total > 0 ? Math.round((stats.ativos / stats.total) * 100) : 0}%</span>
                </div>
                <div className="kpi-card warning">
                    <div className="kpi-icon"><Icon name="star" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">
                            {Object.values(stats.niveis).reduce((a, b) => a + b, 0)}
                        </span>
                        <span className="kpi-label">Com Nível</span>
                    </div>
                </div>
                <div className="kpi-card info">
                    <div className="kpi-icon"><Icon name="attach_money" size={22} /></div>
                    <div className="kpi-info">
                        <span className="kpi-value">{stats.comEmail}</span>
                        <span className="kpi-label">Com Email</span>
                    </div>
                </div>
            </div>

            {/* ==============================================
                FILTROS
                ============================================== */}
            <div className="clientes-filters">
                <div className="search-box">
                    <Icon name="search" size={18} />
                    <input
                        type="text"
                        placeholder="Pesquisar por nome, email, telefone, NUIT..."
                        value={searchTerm || ''}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                        <button className="search-clear" onClick={() => setSearchTerm('')}>
                            <Icon name="close" size={16} />
                        </button>
                    )}
                </div>
                <div className="filter-actions">
                    <button className="btn-filter" onClick={() => setShowFiltros(!showFiltros)}>
                        <Icon name="filter_list" size={18} />
                        {showFiltros ? 'Ocultar Filtros' : 'Filtros'}
                    </button>
                    <button className="btn-refresh" onClick={fetchClientes} title="Atualizar">
                        <Icon name="refresh" size={18} />
                    </button>
                </div>
            </div>

            {/* ==============================================
                FILTROS AVANÇADOS
                ============================================== */}
            {showFiltros && (
                <div className="clientes-filtros-avancados">
                    <div className="filtros-row">
                        <div className="filtro-group">
                            <label>Segmento</label>
                            <select
                                value={filtros.segmento}
                                onChange={(e) => setFiltros({ ...filtros, segmento: e.target.value })}
                            >
                                <option value="todos">Todos os Segmentos</option>
                                <option value="Varejo">🛒 Varejo</option>
                                <option value="Atacado">📦 Atacado</option>
                                <option value="Corporativo">🏢 Corporativo</option>
                                <option value="Premium">⭐ Premium</option>
                                <option value="VIP">👑 VIP</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Fidelidade</label>
                            <select
                                value={filtros.fidelidade}
                                onChange={(e) => setFiltros({ ...filtros, fidelidade: e.target.value })}
                            >
                                <option value="todos">Todos os Níveis</option>
                                <option value="Bronze">🥉 Bronze</option>
                                <option value="Prata">🥈 Prata</option>
                                <option value="Ouro">🥇 Ouro</option>
                                <option value="Platina">💎 Platina</option>
                                <option value="Diamante">👑 Diamante</option>
                            </select>
                        </div>
                        <div className="filtro-group">
                            <label>Status</label>
                            <select
                                value={filtros.status}
                                onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
                            >
                                <option value="todos">Todos os Status</option>
                                <option value="ativo">🟢 Ativo</option>
                                <option value="inativo">🔴 Inativo</option>
                            </select>
                        </div>
                        <button className="btn-aplicar-filtros" onClick={() => aplicarFiltros()}>
                            <Icon name="filter_list" size={16} /> Aplicar
                        </button>
                        <button className="btn-limpar-filtros" onClick={limparFiltros}>
                            <Icon name="clear" size={16} /> Limpar
                        </button>
                    </div>
                </div>
            )}

            {/* ==============================================
                TABELA
                ============================================== */}
            {loading ? (
                <div className="loading-container">
                    <div className="spinner-large"></div>
                    <p>A carregar clientes...</p>
                </div>
            ) : (
                <div className="clientes-table-container">
                    <table className="clientes-table">
                        <thead>
                            <tr>
                                <th className="col-nome">Nome</th>
                                <th className="col-email">Email</th>
                                <th className="col-telefone">Telefone</th>
                                <th className="col-localizacao">Localização</th>
                                <th className="col-status">Status</th>
                                <th className="col-acoes">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentClientes.length === 0 ? (
                                <tr className="empty-row">
                                    <td colSpan="6">
                                        <div className="empty-state">
                                            <Icon name="people" size={48} />
                                            <p>Nenhum cliente encontrado</p>
                                            <button className="btn-create-small" onClick={() => setShowCreateModal(true)}>
                                                Criar primeiro cliente
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                currentClientes.map(cliente => {
                                    const localizacao = [
                                        cliente.cidade,
                                        cliente.provincia
                                    ].filter(Boolean).join(', ') || '-';

                                    const isAtivo = cliente.ativo !== false;

                                    return (
                                        <tr key={cliente._id} className={!isAtivo ? 'cliente-inativo' : ''}>
                                            <td>
                                                <div className="cliente-info">
                                                    <strong>{cliente.nome}</strong>
                                                    <div className="cliente-badges">
                                                        {cliente.nivelFidelidade && (
                                                            <span className={`badge-nivel ${cliente.nivelFidelidade?.toLowerCase()}`}>
                                                                {getNivelIcon(cliente.nivelFidelidade)} {cliente.nivelFidelidade}
                                                            </span>
                                                        )}
                                                        {cliente.segmento && (
                                                            <span className={`badge-segmento ${cliente.segmento?.toLowerCase()}`}>
                                                                {getSegmentoIcon(cliente.segmento)} {cliente.segmento}
                                                            </span>
                                                        )}
                                                        {cliente.metricas?.totalCompras > 10 && (
                                                            <span className="badge-fiel">⭐ Fiel</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{cliente.email}</td>
                                            <td>{cliente.telefone || '-'}</td>
                                            <td>{localizacao}</td>
                                            <td>
                                                <span className={`status-badge ${isAtivo ? 'status-active' : 'status-inactive'}`}>
                                                    <Icon name={isAtivo ? 'check_circle' : 'block'} size={12} />
                                                    {isAtivo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td className="actions-cell">
                                                <button className="action-btn view" onClick={() => verDetalhes(cliente)} title="Ver detalhes">
                                                    <Icon name="visibility" size={16} />
                                                </button>
                                                <button className="action-btn edit" onClick={() => handleEdit(cliente)} title="Editar">
                                                    <Icon name="edit" size={16} />
                                                </button>
                                                <button className="action-btn whatsapp" onClick={() => abrirWhatsApp(cliente)} title="WhatsApp">
                                                    <Icon name="whatsapp" size={16} style={{ color: '#25D366' }} />
                                                </button>
                                                <button className="action-btn email" onClick={() => abrirEmail(cliente)} title="Email">
                                                    <Icon name="email" size={16} style={{ color: '#EA4335' }} />
                                                </button>
                                                <button className="action-btn delete" onClick={() => openDeleteModal(cliente)} title="Excluir">
                                                    <Icon name="delete" size={16} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>

                    {renderPagination()}
                </div>
            )}
        </div>
    );
}